import React from 'react';
import type { Condition, ColumnOption } from './types.ts';
import { STRING_OPERATOR_MAP } from './useSetEditor.ts';
import styles from './styles.less';

interface ConditionBuilderProps {
  condition: Condition;
  conditionIndex: number;
  operationIndex: number;
  operationType: 'node' | 'edge';
  conditionsCount: number;
  columnOptions: ColumnOption[];
  findMatchingColumn: (attributeName: string) => ColumnOption | undefined;
  operatorsByDataType: Record<string, Array<{ label: string; value: string }>>;
  onConditionChange: (operationIndex: number, conditionIndex: number, field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onCaseSensitiveChange: (operationIndex: number, conditionIndex: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCondition: (operationIndex: number, conditionIndex: number) => void;
}

const isBooleanType = (dataType: string | undefined): boolean => {
  return dataType === 'boolean' || dataType === 'bool';
};

const isUnsupportedDataType = (dataType: string): boolean => {
  return dataType === 'datetime' || dataType === 'date' || dataType === 'time';
};

const getUnsupportedDataTypeMessage = (dataType: string): string => {
  switch (dataType) {
    case 'datetime':
    case 'date':
    case 'time':
      return 'DateTime set support is planned. Please contact staff for support.';
    default:
      return 'This data type is not yet supported for sets.';
  }
};

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  condition,
  conditionIndex,
  operationIndex,
  operationType,
  conditionsCount,
  columnOptions,
  findMatchingColumn,
  operatorsByDataType,
  onConditionChange,
  onCaseSensitiveChange,
  onRemoveCondition
}) => {
  const column = findMatchingColumn(condition.attributeName);
  const dataType = column?.dataType;
  const isStringType = dataType === 'string' || !dataType;
  const operatorConfig = STRING_OPERATOR_MAP[condition.operator as keyof typeof STRING_OPERATOR_MAP];
  const showCaseSensitive = isStringType && operatorConfig?.caseSensitive;
  const hideValue = operatorConfig?.hideValue;

  return (
    <div className={styles['condition-container']}>
      <div className={styles['condition-header']}>
        <span className={styles['condition-title']}>Condition {conditionIndex + 1}</span>
        {conditionsCount > 1 && (
          <button type="button" className={styles['remove-condition']} onClick={() => onRemoveCondition(operationIndex, conditionIndex)}>
            Remove Condition
          </button>
        )}
      </div>
      <div className={styles['query-builder-field']}>
        <label htmlFor={`attribute-${operationIndex}-${conditionIndex}`}>Attribute:</label>
        <select
          id={`attribute-${operationIndex}-${conditionIndex}`}
          value={condition.attributeName}
          onChange={onConditionChange(operationIndex, conditionIndex, 'attributeName')}
          className={styles['attribute-select']}
        >
          <option value="">Select attribute</option>
          {columnOptions
            .filter(opt => !opt.componentType || opt.componentType === (operationType === 'edge' ? 'edge' : 'point'))
            .map(option => (
            <option
              key={option.key}
              value={option.value}
              disabled={isUnsupportedDataType(option.dataType)}
              className={isUnsupportedDataType(option.dataType) ? styles['disabled-option'] : ''}
            >
              {option.label} {isUnsupportedDataType(option.dataType) ? '(Not Supported)' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className={styles['query-builder-field']}>
        <label htmlFor={`operator-${operationIndex}-${conditionIndex}`}>Operator:</label>
        <select
          id={`operator-${operationIndex}-${conditionIndex}`}
          value={condition.operator}
          onChange={onConditionChange(operationIndex, conditionIndex, 'operator')}
          className={styles['operator-select']}
        >
          {(operatorsByDataType[dataType] || operatorsByDataType['string']).map(op => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </select>
      </div>
      {!hideValue && (
        <div className={styles['query-builder-field']}>
          <label htmlFor={`value-${operationIndex}-${conditionIndex}`}>{condition.operator === 'In List' ? 'Values (comma-separated):' : 'Value:'}</label>
          {isBooleanType(dataType) ? (
            <select
              id={`value-${operationIndex}-${conditionIndex}`}
              value={condition.value}
              onChange={onConditionChange(operationIndex, conditionIndex, 'value')}
              className={styles['value-input']}
            >
              <option value="">Select value</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          ) : (
            <input
              id={`value-${operationIndex}-${conditionIndex}`}
              type="text"
              value={condition.value}
              onChange={onConditionChange(operationIndex, conditionIndex, 'value')}
              className={styles['value-input']}
              placeholder={condition.operator === 'In List' ? 'value1, value2, ...' : 'Enter value'}
            />
          )}
          {dataType && isUnsupportedDataType(dataType) && (
            <div className={styles['unsupported-data-type-message']}>
              {getUnsupportedDataTypeMessage(dataType)}
            </div>
          )}
        </div>
      )}
      {showCaseSensitive && (
        <div className={styles['query-builder-field']}>
          <label className={styles['case-sensitive-label']}>
            <input
              type="checkbox"
              checked={condition.caseSensitive !== false}
              onChange={(e) => onCaseSensitiveChange(operationIndex, conditionIndex, e)}
            />
            Case Sensitive
          </label>
        </div>
      )}
    </div>
  );
};
